/**
 * 跟进日志页面
 * - Tab1 催收状态：支持暂停/恢复/结案并查看基本信息
 * - Tab2 跟进日志：分页查询与新增日志，单附件上传（≤10MB）
 * - 附件：采用临时业务类型 followuplog_temp 上传，保存时后端迁移到正式目录
 * 说明：
 * - URL 查询参数作为页面状态源：tab 控制当前 Tab，debtId 控制 Tab2 的目标债权
 * - Tab1 的“跟进日志”入口会写入 tab=2&debtId=xxx，保证可刷新/可分享链接
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Card, Tabs, Form, Input, Select, DatePicker, Button, Table, Tag, Space, Descriptions, message, Modal, Dropdown, Pagination, Typography } from 'antd';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { MoreOutlined } from '@ant-design/icons';
import { getMyDebts, getDebtDetail } from '../../services/debt';
import { pauseCollection, resumeCollection, closeCollection, reopenCollection, getFollowupLogs, createFollowupLog, uploadBusinessFile } from '../../services/debt';
import { labelOfDebtStatus, labelOfDebtType, labelOfCollectionStatus, toSelectOptions, FollowupLogTypeOptions, labelOfFollowupType, DebtTypeOptions } from '../../constants/debtEnums';
import { formatAmountText } from '../../utils/numberFormat';
import { PAGINATION_DEFAULT_PAGE_SIZE, PAGINATION_ENABLE_SIZE_CHANGER, PAGINATION_PAGE_SIZE_OPTIONS } from '../../config/theme';
import { useBreakpoint } from '../../hooks/useBreakpoint';

const DEFAULT_PAGINATION = { current: 1, pageSize: PAGINATION_DEFAULT_PAGE_SIZE, total: 0 };
const DEFAULT_SORTER = { field: 'update_time', order: 'desc' };
const normalizePageSize = (size) => (PAGINATION_ENABLE_SIZE_CHANGER ? Number(size || PAGINATION_DEFAULT_PAGE_SIZE) : PAGINATION_DEFAULT_PAGE_SIZE);

const DebtFollowupPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || '1');
  const [selectedDebtId, setSelectedDebtId] = useState(searchParams.get('debtId') || null);

  useEffect(() => {
    // 保持组件 state 与 URL 同步：支持浏览器前进/后退、手动修改 query、分享链接直达
    const nextTab = searchParams.get('tab') || '1';
    const nextDebtId = searchParams.get('debtId') || null;
    setActiveTab(nextTab);
    setSelectedDebtId(nextDebtId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const onTabChange = (key) => {
    // Tab2 依赖已选债权：避免在未选择债权时进入空白日志页
    if (key === '2' && !selectedDebtId) {
      message.warning('请先选择债权');
      const sp = new URLSearchParams(location.search);
      sp.set('tab', '1');
      navigate({ search: sp.toString() });
      return;
    }
    const sp = new URLSearchParams(location.search);
    sp.set('tab', key);
    navigate({ search: sp.toString() });
  };

  useEffect(() => {
    // 兜底保护：当外部直接切到 tab=2 但缺少 debtId 时，自动回到 Tab1
    if (activeTab === '2' && !selectedDebtId) {
      message.warning('请先选择债权');
      const sp = new URLSearchParams(location.search);
      sp.set('tab', '1');
      navigate({ search: sp.toString() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedDebtId]);

  return (
    <div className="page-container p-debt-management-followup">
      <div className="page-content page-content-wide space-y-md">
        <div className="page-title">跟进日志</div>
        <Card className="card-compact">
          <Tabs activeKey={activeTab} onChange={onTabChange}>
            <Tabs.TabPane tab="催收状态" key="1">
              <CollectionStatusTab navigate={navigate} />
            </Tabs.TabPane>
            <Tabs.TabPane tab="跟进日志" key="2">
              <FollowupLogsTab debtId={selectedDebtId} navigate={navigate} location={location} />
            </Tabs.TabPane>
          </Tabs>
        </Card>
      </div>
    </div>
  );
};

/**
 * 催收状态列表的操作列：智能展示 + 溢出折叠
 * - 候选操作由催收状态决定（暂停/恢复/结案/恢复催收/跟进日志）
 * - 容器宽度不足时，将尾部操作折叠到“更多”下拉
 */
const ActionCell = ({ record, layoutKey, actionWidth, onOpenFollowup, onPause, onResume, onClose, onReopen }) => {
  const containerRef = useRef(null);

  const candidates = useMemo(() => {
    const s = String(record?.collectionStatus || '');
    if (s === 'CLOSED') {
      return [
        { key: 'reopen', label: '恢复催收' },
        { key: 'followupLogs', label: '跟进日志' },
      ];
    }
    if (s === 'PAUSED') {
      return [
        { key: 'resume', label: '恢复催收' },
        { key: 'close', label: '结案' },
        { key: 'followupLogs', label: '跟进日志' },
      ];
    }
    return [
      { key: 'pause', label: '暂停催收' },
      { key: 'close', label: '结案' },
      { key: 'followupLogs', label: '跟进日志' },
    ];
  }, [record?.collectionStatus]);

  const [inlineKeys, setInlineKeys] = useState(() => candidates.map(a => a.key));

  useEffect(() => {
    // 响应候选操作与布局变化（移动端/桌面）重新计算可展示的操作集合
    setInlineKeys(candidates.map(a => a.key));
  }, [candidates, layoutKey]);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    // 溢出判断：如果超出容器宽度，则逐步减少 inline 操作，把最后一个挪到下拉里
    const isOverflow = el.scrollWidth > el.clientWidth + 1;
    if (isOverflow && inlineKeys.length > 1) {
      const next = inlineKeys.slice(0, -1);
      if (next.join('|') !== inlineKeys.join('|')) {
        setInlineKeys(next);
      }
    }
  }, [candidates, inlineKeys]);

  const inlineActions = candidates.filter(a => inlineKeys.includes(a.key));
  const dropdownActions = candidates.filter(a => !inlineKeys.includes(a.key));

  const handleAction = async (key) => {
    if (key === 'followupLogs') {
      onOpenFollowup?.(record);
      return;
    }
    if (key === 'pause') {
      await onPause?.(record);
      return;
    }
    if (key === 'resume') {
      await onResume?.(record);
      return;
    }
    if (key === 'close') {
      await onClose?.(record);
      return;
    }
    if (key === 'reopen') {
      await onReopen?.(record);
    }
  };

  return (
    <div
      ref={containerRef}
      className="c-actions-inline c-actions-cell"
      style={{ '--actions-cell-width': actionWidth }}
    >
      {inlineActions.map(action => (
        <Button
          key={action.key}
          type="link"
          className="c-btn-link-compact"
          onClick={() => handleAction(action.key)}
        >
          {action.label}
        </Button>
      ))}

      {dropdownActions.length > 0 && (
        <Dropdown
          trigger={['click']}
          menu={{
            items: dropdownActions.map(a => ({ key: a.key, label: a.label })),
            onClick: async ({ key }) => {
              await handleAction(key);
            },
          }}
        >
          <Button type="link" className="c-btn-link-compact c-actions-more" icon={<MoreOutlined />} aria-label="更多操作" />
        </Dropdown>
      )}
    </div>
  );
};

/**
 * Tab1：催收状态
 * 职责：筛选并展示当前用户“已发布/待审核”的债权，提供催收状态流转与进入跟进日志入口。
 */
const CollectionStatusTab = ({ navigate }) => {
  const { isMobile } = useBreakpoint();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [sorter, setSorter] = useState(DEFAULT_SORTER);
  const [dateRangeSelecting, setDateRangeSelecting] = useState(null);

  const disabledOccurrenceDate = useCallback((current) => {
    if (!current) return false;
    const range = Array.isArray(dateRangeSelecting) ? dateRangeSelecting : [];
    const start = range[0];
    const end = range[1];
    if (start && !end) {
      // 交互优化：当用户先选了开始日期但还没选结束日期时，限制可选范围在开始日期±1个月
      const min = start.subtract(1, 'month');
      const max = start.add(1, 'month');
      return current.isBefore(min, 'day') || current.isAfter(max, 'day');
    }
    return false;
  }, [dateRangeSelecting]);

  const fetchList = useCallback(async (pageCfg, sortCfg) => {
    try {
      setLoading(true);
      const values = form.getFieldsValue();
      const params = {
        page: pageCfg.current,
        size: pageCfg.pageSize,
        debt_type: values.debt_type,
        keyword: values.keyword,
        // 发生日：后端按天解析并做范围校验（开始/结束日期均存在时 ≤ 1 个月）
        start_date: values.date_range && values.date_range[0] ? values.date_range[0].format('YYYY-MM-DD') : undefined,
        end_date: values.date_range && values.date_range[1] ? values.date_range[1].format('YYYY-MM-DD') : undefined,
        sort_field: sortCfg.field,
        sort_order: sortCfg.order,
        need_total: pageCfg.current === 1,
      };
      const { data } = await getMyDebts(params);
      const all = Array.isArray(data?.records) ? data.records : [];
      const recs = all.filter(r => r.status === 'PUBLISHED' || r.status === 'PENDING');
      setRecords(recs);
      const totalFromApi = data?.total;
      setPagination({
        current: Number(data?.current || pageCfg.current),
        pageSize: normalizePageSize(data?.size || pageCfg.pageSize),
        total: Number(totalFromApi ?? (pageCfg.total ?? 0)),
      });
    } catch (e) {
      message.error('加载我的债权失败');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    // 首次进入：按默认分页与排序拉取列表
    fetchList({ current: 1, pageSize: DEFAULT_PAGINATION.pageSize }, DEFAULT_SORTER);
  }, [fetchList]);

  const handleSearch = (values) => {
    const range = values?.date_range;
    const start = Array.isArray(range) ? range[0] : null;
    const end = Array.isArray(range) ? range[1] : null;
    if (start && end) {
      // 前端先做一次提示校验，避免无效请求；后端仍会兜底校验
      if (end.isBefore(start, 'day')) {
        message.error('发生日开始日期不能晚于结束日期');
        return;
      }
      if (end.isAfter(start.add(1, 'month'), 'day')) {
        message.error('发生日查询范围不能超过一个月');
        return;
      }
    }
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchList({ current: 1, pageSize: pagination.pageSize }, sorter);
  };

  const handleReset = () => {
    form.resetFields();
    setDateRangeSelecting(null);
    setPagination(prev => ({ ...prev, current: 1 }));
    setSorter(DEFAULT_SORTER);
    fetchList({ current: 1, pageSize: pagination.pageSize }, DEFAULT_SORTER);
  };

  const handleTableChange = (pag, filters, sorterArg) => {
    // antd Table sorter.order: ascend/descend -> 后端 sort_order: asc/desc
    const order = sorterArg?.order ? (sorterArg.order === 'ascend' ? 'asc' : 'desc') : sorter.order;
    const field = sorterArg?.field || sorter.field;
    const nextSorter = { field, order };
    setSorter(nextSorter);
    fetchList(pagination, nextSorter);
  };

  const doPause = async (id) => {
    try {
      await pauseCollection(id);
      message.success('已暂停催收');
      fetchList({ current: pagination.current, pageSize: pagination.pageSize }, sorter);
    } catch (e) {
      message.error('暂停催收失败');
    }
  };
  const doResume = async (id) => {
    try {
      await resumeCollection(id);
      message.success('已恢复催收');
      fetchList({ current: pagination.current, pageSize: pagination.pageSize }, sorter);
    } catch (e) {
      message.error('恢复催收失败');
    }
  };
  const doClose = async (id) => {
    try {
      await closeCollection(id);
      message.success('已结案');
      fetchList({ current: pagination.current, pageSize: pagination.pageSize }, sorter);
    } catch (e) {
      message.error('结案失败');
    }
  };
  const doReopen = async (id) => {
    try {
      await reopenCollection(id);
      message.success('已恢复催收');
      fetchList({ current: pagination.current, pageSize: pagination.pageSize }, sorter);
    } catch (e) {
      message.error('恢复催收失败');
    }
  };

  const handlePaginationChange = (page, pageSize) => {
    const nextPageSize = normalizePageSize(pageSize);
    const shouldResetToFirstPage = nextPageSize !== pagination.pageSize;
    const nextCurrent = shouldResetToFirstPage ? 1 : page;
    const nextPagination = { ...pagination, current: nextCurrent, pageSize: nextPageSize };
    setPagination(nextPagination);
    fetchList(nextPagination, sorter);
  };

  const columns = [
    { title: '编号', dataIndex: 'debtNo', key: 'debtNo', width: 160 },
    {
      title: '债权名称',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
      render: (v, record) => (
        <Typography.Link className="u-ellipsis-inline font-medium" onClick={() => navigate(`/debt-management/detail/${record.id}`)}>
          {v || '-'}
        </Typography.Link>
      ),
    },
    { title: '债权类型', dataIndex: 'debtType', key: 'debtType', width: 140, render: (v) => labelOfDebtType(v) },
    { title: '本金金额', dataIndex: 'principalAmount', key: 'principalAmount', width: 120, sorter: true, align: 'right', className: 'u-tabular-nums', render: (v) => formatAmountText(v) },
    { title: '合计金额', dataIndex: 'totalAmount', key: 'totalAmount', width: 120, sorter: true, align: 'right', className: 'u-tabular-nums', render: (v) => formatAmountText(v) },
    { title: '发生日', dataIndex: 'debtOccurrenceDate', key: 'debtOccurrenceDate', width: 120, sorter: true, render: (v) => v ? dayjs(v).format('YYYY-MM-DD') : '' },
    { title: '债权状态', dataIndex: 'status', key: 'status', width: 120, render: (v) => {
      const color = v === 'PUBLISHED' ? 'green' : v === 'PENDING' ? 'orange' : 'default';
      return <Tag color={color}>{labelOfDebtStatus(v)}</Tag>;
    } },
    { title: '催收状态', dataIndex: 'collectionStatus', key: 'collectionStatus', width: 120, render: (v) => {
      const color = v === 'IN_COLLECTION' ? 'green' : v === 'PAUSED' ? 'orange' : v === 'CLOSED' ? 'red' : 'default';
      return <Tag color={color}>{labelOfCollectionStatus(v)}</Tag>;
    } },    
    { title: '更新时间', dataIndex: 'updateTime', key: 'updateTime', width: 160, sorter: true, render: (v) => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '' },
    { title: '操作', key: 'action', width: isMobile ? 220 : 340, render: (_, record) => (
      <ActionCell
        record={record}
        layoutKey={isMobile ? 'mobile' : 'desktop'}
        actionWidth={isMobile ? '220px' : '340px'}
        onPause={() => doPause(record.id)}
        onResume={() => doResume(record.id)}
        onClose={() => doClose(record.id)}
        onReopen={() => doReopen(record.id)}
        onOpenFollowup={() => {
          // 通过 URL 驱动 Tab2：支持刷新/复制链接后仍能定位到指定债权日志
          const sp = new URLSearchParams();
          sp.set('tab', '2');
          sp.set('debtId', String(record.id));
          navigate({ search: sp.toString() });
        }}
      />
    ) },
  ];

  return (
    <>
      <Card className="card-compact" style={{ marginBottom: 12 }}>
        <Form layout={isMobile ? 'vertical' : 'inline'} form={form} onFinish={handleSearch}>
          <Form.Item name="keyword" label="关键词">
            <Input placeholder="债权名称/编号" allowClear style={{ width: isMobile ? '100%' : 200 }} />
          </Form.Item>
          <Form.Item name="debt_type" label="类型">
            <Select allowClear style={{ width: isMobile ? '100%' : 180 }} options={toSelectOptions(DebtTypeOptions)} />
          </Form.Item>
          <Form.Item name="date_range" label="发生日">
            <DatePicker.RangePicker
              allowEmpty={[true, true]}
              popupClassName="c-range-picker-single-panel"
              style={{ width: isMobile ? '100%' : undefined }}
              disabledDate={disabledOccurrenceDate}
              onCalendarChange={(dates) => setDateRangeSelecting(dates)}
              onChange={(dates) => setDateRangeSelecting(dates)}
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">查询</Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
      <Card className="card-compact">
        <Table
          rowKey="id"
          loading={loading}
          dataSource={records}
          columns={columns}
          size={isMobile ? 'small' : 'middle'}
          pagination={false}
          scroll={{ x: 'max-content' }}
          onChange={handleTableChange}
        />
        <div className="c-pagination-bar">
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            simple={isMobile}
            showSizeChanger={isMobile ? false : PAGINATION_ENABLE_SIZE_CHANGER}
            pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
            onChange={handlePaginationChange}
          />
        </div>
      </Card>

    </>
  );
};

/**
 * Tab2：跟进日志
 * 职责：展示指定债权的跟进日志，支持筛选、分页、新增日志与单附件上传。
 */
const FollowupLogsTab = ({ debtId, navigate, location }) => {
  const { isMobile } = useBreakpoint();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [pagination, setPagination] = useState(DEFAULT_PAGINATION);
  const [detail, setDetail] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [createLoading, setCreateLoading] = useState(false);
  const [createForm] = Form.useForm();
  const { user } = useSelector(state => state.auth);
  const [attachmentTemp, setAttachmentTemp] = useState(null);

  useEffect(() => {
    // debtId 变更时，刷新债权信息与日志列表；无 debtId 则清空展示
    if (debtId) {
      loadDebtInfo(debtId);
      fetchLogs(debtId, { current: 1, pageSize: pagination.pageSize });
    } else {
      setDetail(null);
      setRecords([]);
      setPagination(prev => ({ ...prev, current: 1, total: 0 }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debtId]);

  const loadDebtInfo = async (id) => {
    try {
      const { data } = await getDebtDetail(id);
      setDetail(data);
    } catch (_) {
      message.error('获取债权信息失败');
    }
  };

  const fetchLogs = async (id, pageCfg = pagination) => {
    if (!id) return;
    try {
      setLoading(true);
      const values = form.getFieldsValue();
      const params = {
        page: pageCfg.current,
        size: pageCfg.pageSize,
        keyword: values.keyword,
        log_type: values.log_type,
        need_total: pageCfg.current === 1,
      };
      const { data } = await getFollowupLogs(id, params);
      const list = Array.isArray(data?.records) ? data.records : [];
      setRecords(list);
      const totalFromApi = data?.total;
      setPagination({
        current: Number(data?.current || pageCfg.current),
        pageSize: normalizePageSize(data?.size || pageCfg.pageSize),
        total: Number(totalFromApi ?? (pageCfg.total ?? 0)),
      });
    } catch (e) {
      message.error('加载跟进日志失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchLogs(debtId, { current: 1, pageSize: pagination.pageSize });
  };

  const handleReset = () => {
    form.resetFields();
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchLogs(debtId, { current: 1, pageSize: pagination.pageSize });
  };

  const handlePaginationChange = (page, pageSize) => {
    const nextPageSize = normalizePageSize(pageSize);
    const shouldResetToFirstPage = nextPageSize !== pagination.pageSize;
    const nextCurrent = shouldResetToFirstPage ? 1 : page;
    const nextPagination = { ...pagination, current: nextCurrent, pageSize: nextPageSize };
    setPagination(nextPagination);
    fetchLogs(debtId, nextPagination);
  };

  const openCreate = () => {
    if (!debtId) {
      message.warning('请先在Tab1选择“跟进日志”进入此页');
      return;
    }
    createForm.resetFields();
    setAttachmentTemp(null);
    setCreateOpen(true);
  };

  /**
   * 单文件上传（临时目录）
   * - 校验：单个文件 ≤10MB；清空 input 以允许重复选择同名文件
   * - 成功：记录临时URL与原文件名，预览使用签名URL
   */
  const handleUploadFiles = async (e) => {
    const file = (e.target.files && e.target.files[0]) || null;
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      message.error('单个文件不得超过10MB');
      e.target.value = '';
      return;
    }
    const uid = user?.id || 0;
    try {
      const res = await uploadBusinessFile(file, 'followuplog_temp', uid);
      const item = { temp_url: res?.data?.url || '', file_name: file.name };
      setAttachmentTemp(item);
      message.success('附件上传成功');
    } catch (_) {
      message.error('附件上传失败');
    } finally {
      e.target.value = '';
    }
  };

  const removeTempAttachment = () => {
    setAttachmentTemp(null);
  };

  /**
   * 提交新增跟进日志
   * - 附件：仅传临时URL与原始文件名，后端负责迁移并落库对象键
   * - 时间：使用 ISO 字符串，后端解析为 LocalDateTime
   */
  const submitCreate = async () => {
    try {
      const values = await createForm.validateFields();
      setCreateLoading(true);
      const payload = {
        log_type: values.log_type,
        title: values.title,
        content: values.content,
        attachment_file_url: attachmentTemp ? attachmentTemp.temp_url : undefined,
        attachment_name: attachmentTemp ? attachmentTemp.file_name : undefined,
        contact_target: values.contact_target,
        happen_time: values.happen_time ? values.happen_time.toISOString() : undefined,
      };
      await createFollowupLog(debtId, payload);
      message.success('已新增跟进日志');
      setCreateOpen(false);
      setAttachmentTemp(null);
      fetchLogs(debtId, { current: 1, pageSize: pagination.pageSize });
    } catch (e) {
      if (e?.errorFields) return;
      message.error('新增跟进日志失败');
    } finally {
      setCreateLoading(false);
    }
  };

  const columns = [
    { title: '日志类型', dataIndex: 'logType', key: 'logType', width: 140, render: (v) => labelOfFollowupType(v) },
    { title: '日志标题', dataIndex: 'title', key: 'title', width: 100, ellipsis: true },
    { title: '日志内容', dataIndex: 'content', key: 'content', width: 190, ellipsis: true },
    { title: '附件', dataIndex: 'attachmentFileUrl', key: 'attachmentFileUrl', width: 180, ellipsis: true, render: (url, record) => {
      if (!url) return '';
      const name = record?.attachmentName || '附件';
      return (
        <a className="u-ellipsis-inline" href={url} target="_blank" rel="noreferrer" title={name}>
          {name}
        </a>
      );
    } },
    { title: '联系对象/渠道', dataIndex: 'contactTarget', key: 'contactTarget', width: 200, ellipsis: true },
    { title: '事件时间', dataIndex: 'happenTime', key: 'happenTime', width: 160, render: (v) => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '' },
    { title: '创建时间', dataIndex: 'createTime', key: 'createTime', width: 160, render: (v) => v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '' },
  ];

  return (
    <>
      <Card className="card-compact" style={{ marginBottom: 12 }}>
        <Space style={{ marginBottom: 12 }} wrap>
          <Button onClick={() => navigate({ search: 'tab=1' })}>返回催收状态</Button>
        </Space>
        {detail && (
          <Descriptions
            title="已选债权信息"
            bordered
            size="small"
            layout="horizontal"
            column={isMobile ? 1 : 2}
            style={{ marginBottom: 12 }}
          >
            <Descriptions.Item label="编号">
              <span className="u-nowrap">{detail?.debt?.debtNo}</span>
            </Descriptions.Item>
            <Descriptions.Item label="债权名称" span={isMobile ? 1 : 2}>
              <Typography.Link className="u-ellipsis-inline" onClick={() => navigate(`/debt-management/detail/${detail?.debt?.id}`)}>
                {detail?.debt?.title || '-'}
              </Typography.Link>
            </Descriptions.Item>
            <Descriptions.Item label="债权类型">{labelOfDebtType(detail?.debt?.debtType)}</Descriptions.Item>
            <Descriptions.Item label="催收状态">{labelOfCollectionStatus(detail?.debt?.collectionStatus)}</Descriptions.Item>
            <Descriptions.Item label="合计金额">{formatAmountText(detail?.debt?.totalAmount)}</Descriptions.Item>
          </Descriptions>
        )}
        <Form layout={isMobile ? 'vertical' : 'inline'} form={form} onFinish={handleSearch}>
          <Form.Item name="keyword" label="关键词">
            <Input placeholder="标题/内容" allowClear style={{ width: isMobile ? '100%' : 240 }} />
          </Form.Item>
          <Form.Item name="log_type" label="类型">
            <Select allowClear style={{ width: isMobile ? '100%' : 200 }} options={FollowupLogTypeOptions} />
          </Form.Item>
          <Form.Item>
            <Space wrap>
              <Button type="primary" htmlType="submit">查询</Button>
              <Button onClick={handleReset}>重置</Button>
              <Button onClick={openCreate}>新增跟进日志</Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>
      <Card className="card-compact">
        <Table
          rowKey="id"
          loading={loading}
          dataSource={records}
          columns={columns}
          size={isMobile ? 'small' : 'middle'}
          pagination={false}
          tableLayout="fixed"
          scroll={{ x: 'max-content' }}
        />
        <div className="c-pagination-bar">
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={pagination.total}
            simple={isMobile}
            showSizeChanger={isMobile ? false : PAGINATION_ENABLE_SIZE_CHANGER}
            pageSizeOptions={PAGINATION_PAGE_SIZE_OPTIONS}
            onChange={handlePaginationChange}
          />
        </div>
      </Card>

      <Modal
        title="新增跟进日志"
        open={createOpen}
        onOk={submitCreate}
        onCancel={() => setCreateOpen(false)}
        confirmLoading={createLoading}
        okText="保存"
        cancelText="取消"
      >
        <Form form={createForm} layout="vertical">
          <Form.Item name="log_type" label="日志类型" rules={[{ required: true }]}>
            <Select options={FollowupLogTypeOptions} placeholder="请选择日志类型" />
          </Form.Item>
          <Form.Item name="title" label="日志标题" rules={[{ required: true }]}>
            <Input placeholder="请输入日志标题" />
          </Form.Item>
          <Form.Item name="content" label="日志内容" rules={[{ required: true }]}>
            <Input.TextArea rows={4} placeholder="请填写日志内容" />
          </Form.Item>
          <Form.Item label="上传附件">
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <input type="file" onChange={handleUploadFiles}
                     accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.xls,.xlsx,.zip,.rar,.7z,application/zip,application/x-zip-compressed,application/x-rar-compressed,application/x-7z-compressed,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/pdf,image/*" />
              <span style={{ color: '#666' }}>仅支持单文件，支持压缩文件，单个不超过10MB</span>
            </div>
            <div style={{ marginTop: 8 }}>
              {attachmentTemp ? (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span>{attachmentTemp.file_name}</span>
                  <Space>
                    <a href={attachmentTemp.temp_url} target="_blank" rel="noreferrer">预览</a>
                    <Button size="small" type="link" danger onClick={removeTempAttachment}>移除</Button>
                  </Space>
                </div>
              ) : (
                <span style={{ color: '#999' }}>未上传附件</span>
              )}
            </div>
          </Form.Item>
          <Form.Item name="contact_target" label="联系对象/渠道">
            <Input placeholder="例如：电话/邮箱/公司名等" />
          </Form.Item>
          <Form.Item name="happen_time" label="事件发生时间">
            <DatePicker showTime />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default DebtFollowupPage;
